import React, { useState, useEffect } from 'react';
import { T, getStatusClass, Th, Tr, ActionBtn, Modal, UserDetailFields, TableCard, SearchInput, Toast, PrimaryBtn } from './theme';
import { useRefresh } from './RefreshContext';
import API_BASE_URL from '../config/api';

const AdminUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [updates, setUpdates] = useState({});
  const [originalUpdates, setOriginalUpdates] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const { refresh, triggerRefresh } = useRefresh();
  const PER_PAGE = 20;

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE_URL}/vrv-users-getAll`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      setUsers(parsed.users || []);
    } catch { setMessage('Error fetching users'); }
    setLoading(false);
  };

  useEffect(() => { fetchAllUsers(); }, [refresh]); // eslint-disable-line

  const filtered = users.filter(u => !search || Object.values(u).some(v => String(v).toLowerCase().includes(search.toLowerCase())));
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const start = (currentPage - 1) * PER_PAGE;
  const paginated = filtered.slice(start, start + PER_PAGE);
  useEffect(() => setCurrentPage(1), [search]);

  const openEdit = (user) => {
    setEditUser(user);
    const init = { role: user.role || '', status: user.status || '' };
    setUpdates(init); setOriginalUpdates(init); setMessage('');
  };
  const closeEdit = () => { setEditUser(null); setUpdates({}); setOriginalUpdates({}); setMessage(''); };
  const hasChanges = () => Object.keys(originalUpdates).some(k => updates[k] !== originalUpdates[k]);
  const handleChange = e => setUpdates({ ...updates, [e.target.name]: e.target.value });

  const handleUpdate = async (e) => {
    e.preventDefault(); setMessage('');
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE_URL}/vrv-users-update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ emailID: editUser.emailID, requestorRole: 'ADMIN', updates: { role: updates.role, status: updates.status } }),
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ show: true, message: 'User updated!', type: 'success' });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
        fetchAllUsers(); triggerRefresh(); setTimeout(closeEdit, 1200);
      } else setMessage(data.message || 'Update failed');
    } catch { setMessage('Error connecting to server'); }
  };

  const FieldBox = ({ label, children }) => (
    <div>
      <label className="vrv-label">{label}</label>
      {children}
    </div>
  );

  const RolePills = () => {
    const currentRoles = (updates.role || '').split(',').map(s => s.trim()).filter(Boolean);
    return (
      <div className="flex flex-wrap gap-2 p-3 rounded-xl" style={{ background: '#F8F5EE', border: '1px solid rgba(0,0,0,0.07)' }}>
        {['USER', 'ADMIN', 'CONTENT-CREATOR', 'CONTENT-APPROVER'].map(r => {
          const checked = currentRoles.includes(r);
          return (
            <label key={r} className="flex items-center gap-1.5 text-xs font-medium cursor-pointer select-none" style={{ color: T.ink }}>
              <input type="checkbox" checked={checked} onChange={() => {
                const nr = checked ? currentRoles.filter(x => x !== r) : [...currentRoles, r];
                setUpdates({ ...updates, role: nr.join(',') });
              }} className="accent-gold" />
              {r}
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {/* Search */}
      <div className="mb-5">
        <SearchInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users by name, email, role…" />
      </div>

      {loading && (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => <div key={i} className="shimmer h-14 rounded-2xl" />)}
        </div>
      )}

      {!loading && users.length > 0 && (
        <>
          {/* Desktop Table */}
          <div className="desktop-table">
            <TableCard>
              <div className="overflow-x-auto">
                <table className="w-full text-sm vrv-table-sticky">
                  <thead>
                    <tr>
                      <Th>#</Th><Th>Member</Th><Th>Gothram</Th><Th>Email</Th><Th>Phone</Th><Th>Role</Th><Th>Status</Th><Th center>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((user, i) => (
                      <Tr key={user.emailID || i}>
                        <td className="px-4 py-3 text-xs" style={{ color: T.muted }}>{start + i + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="vrv-avatar flex-shrink-0">
                              {(user.name || user.emailID || '?').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-sm leading-tight" style={{ color: T.ink }}>{user.name} {user.surname}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: T.muted }}>{user.gothram || '—'}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: T.muted }}>{user.emailID}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: T.muted }}>{user.phoneNumber || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`badge ${user.role?.includes('ADMIN') ? 'badge-gold' : 'badge-navy'}`}>{user.role?.split(',')[0]}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={getStatusClass(user.status)}>{user.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5 justify-center">
                            <ActionBtn onClick={() => setViewUser(user)} color="muted" small>View</ActionBtn>
                            <ActionBtn onClick={() => openEdit(user)} color="gold" small>Edit</ActionBtn>
                          </div>
                        </td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between px-5 py-3" style={{ background: '#F1EDE4', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                <p className="text-xs" style={{ color: T.muted }}>
                  {start + 1}–{Math.min(start + PER_PAGE, filtered.length)} of {filtered.length} users
                </p>
                <div className="flex gap-1">
                  <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
                    className="px-3 py-1.5 text-[11px] font-bold rounded-xl transition-all disabled:opacity-40"
                    style={{ border: '1px solid rgba(0,0,0,0.1)', color: T.muted }}>‹ Prev</button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pg = currentPage <= 3 ? i + 1 : currentPage + i - 2;
                    if (pg < 1 || pg > totalPages) return null;
                    return (
                      <button key={pg} onClick={() => setCurrentPage(pg)}
                        className="px-3 py-1.5 text-[11px] font-bold rounded-xl transition-all"
                        style={currentPage === pg ? { background: T.navy, color: 'white', border: `1px solid ${T.navy}` } : { border: '1px solid rgba(0,0,0,0.1)', color: T.muted }}>
                        {pg}
                      </button>
                    );
                  })}
                  <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-[11px] font-bold rounded-xl transition-all disabled:opacity-40"
                    style={{ border: '1px solid rgba(0,0,0,0.1)', color: T.muted }}>Next ›</button>
                </div>
              </div>
            </TableCard>
          </div>

          {/* Mobile Cards */}
          <div className="mobile-cards space-y-4">
            {paginated.map((user, i) => (
              <div key={user.emailID || i} className="vrv-card p-5" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, #D4AF37, #B9932F)`, color: '#0F172A' }}>
                    {(user.name || user.emailID || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>{user.name} {user.surname}</p>
                    <p className="text-xs truncate" style={{ color: T.muted }}>{user.emailID}</p>
                  </div>
                  <span className={getStatusClass(user.status)}>{user.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                  <div><span style={{ color: T.muted }}>Gothram: </span><span style={{ color: T.ink }}>{user.gothram || '—'}</span></div>
                  <div><span style={{ color: T.muted }}>Phone: </span><span style={{ color: T.ink }}>{user.phoneNumber || '—'}</span></div>
                  <div><span style={{ color: T.muted }}>Role: </span><span className={`badge ${user.role?.includes('ADMIN') ? 'badge-gold' : 'badge-navy'}`} style={{ fontSize: '9px' }}>{user.role?.split(',')[0]}</span></div>
                </div>
                <div className="flex gap-2 pt-3" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  <ActionBtn onClick={() => setViewUser(user)} color="muted">View</ActionBtn>
                  <ActionBtn onClick={() => openEdit(user)} color="gold">Edit</ActionBtn>
                </div>
              </div>
            ))}

            {/* Mobile Pagination */}
            <div className="flex items-center justify-center gap-2 pt-2">
              <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
                className="btn-outline text-xs px-4 py-2" style={{ minHeight: '40px' }}>‹ Prev</button>
              <span className="text-xs" style={{ color: T.muted }}>Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                className="btn-outline text-xs px-4 py-2" style={{ minHeight: '40px' }}>Next ›</button>
            </div>
          </div>
        </>
      )}

      {!loading && users.length === 0 && (
        <div className="vrv-card text-center py-16">
          <p className="text-4xl mb-3">👥</p>
          <p className="font-semibold" style={{ color: T.muted }}>No users found</p>
        </div>
      )}

      {/* View Modal */}
      {viewUser && (
        <Modal onClose={() => setViewUser(null)} title="User Details" subtitle="Member Info" wide>
          <UserDetailFields user={viewUser} />
          <div className="mt-6 flex justify-end"><PrimaryBtn onClick={() => setViewUser(null)}>Close</PrimaryBtn></div>
        </Modal>
      )}

      {/* Edit Modal */}
      {editUser && (
        <Modal onClose={closeEdit} title="Edit User" subtitle="Admin Edit" wide>
          <form onSubmit={handleUpdate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[['Name', 'name', 'text'], ['Surname', 'surname', 'text'], ['Phone Number', 'phoneNumber', 'tel'], ['Date of Birth', 'dob', 'text']].map(([label, name, type]) => (
              <FieldBox key={name} label={label}>
                <input type={type} name={name} value={editUser[name] || ''} readOnly
                  className="vrv-input" style={{ opacity: 0.6, cursor: 'not-allowed', background: '#F8F5EE' }} />
              </FieldBox>
            ))}
            <FieldBox label="Gothram">
              <input type="text" name="gothram" value={editUser.gothram || ''} readOnly
                className="vrv-input" style={{ opacity: 0.6, cursor: 'not-allowed', background: '#F8F5EE' }} />
            </FieldBox>
            <FieldBox label="Status">
              <select name="status" value={updates.status} onChange={handleChange} className="vrv-select">
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </FieldBox>
            <div className="md:col-span-2">
              <FieldBox label="Roles"><RolePills /></FieldBox>
            </div>
            <div className="md:col-span-2">
              <FieldBox label="Address">
                <textarea name="address" value={editUser.address || ''} readOnly rows="3"
                  className="vrv-input resize-none" style={{ opacity: 0.6, cursor: 'not-allowed', background: '#F8F5EE' }} />
              </FieldBox>
            </div>
            {message && (
              <div className="md:col-span-2 px-4 py-3 rounded-xl text-sm"
                style={{ background: message.includes('success') ? '#f0fdf4' : '#fef2f2', color: message.includes('success') ? '#15803d' : '#b91c1c', border: `1px solid ${message.includes('success') ? '#bbf7d0' : '#fecaca'}` }}>
                {message}
              </div>
            )}
            <div className="md:col-span-2 flex justify-end gap-3 pt-2">
              <ActionBtn onClick={closeEdit} color="muted">Cancel</ActionBtn>
              <button type="submit" disabled={!hasChanges()} className="btn-navy" style={{ opacity: hasChanges() ? 1 : 0.45 }}>
                Update User →
              </button>
            </div>
          </form>
        </Modal>
      )}

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default AdminUsersTab;
